/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../../logging/Log";
import type { IPlayer } from "../../interfaces";
import { Direction, SpeedLevel, TrickPlayMode } from "../../enums";
import { SPEED_VALUE } from "../../types";
import type { ITrickPlayControl, ITrickPlayConfig } from "../../interfaces";

/**
 * Emulated trick-play implementation.
 *
 * Used as the fallback when neither thumbnail service nor I-frame
 * trick-play track is available. The player is moved to the target
 * position during the session via repeated seek calls.
 *
 * No visual preview is rendered by this impl — the UI shows
 * position/timestamp indicators only.
 */
export class EmulatedTrickplayControlImpl implements ITrickPlayControl {
  private static readonly TAG: string = "EmulatedTrickplayControlImpl";

  public readonly mode: TrickPlayMode = TrickPlayMode.Emulated;

  /** Reference to the active player instance. */
  private mPlayer: IPlayer;

  /** Trick-play configuration values. */
  private mConfig: ITrickPlayConfig;

  /** Handle for the active setInterval tick, or null if idle. */
  private mTickId: ReturnType<typeof setInterval> | null = null;

  constructor(player: IPlayer, config: ITrickPlayConfig) {
    this.mPlayer = player;
    this.mConfig = config;
  }

  // ── Private Methods ──────────────────────────────────────────────────────

  private startTick(fn: () => void, ms: number): void {
    this.clearTick();
    this.mTickId = setInterval(fn, ms);
    fn();
  }

  private clearTick(): void {
    if (this.mTickId !== null) {
      clearInterval(this.mTickId);
      this.mTickId = null;
    }
  }

  /**
   * Clamp a target position to the seekable range boundaries.
   * If the target exceeds a boundary, the tick is cleared (auto-stop).
   *
   * @returns The clamped position.
   */
  private clampToSeekRange(target: number): number {
    const range = this.mPlayer.getSeekableRange();
    if (target <= range.start) {
      this.clearTick();
      return range.start;
    }
    if (target >= range.end) {
      this.clearTick();
      return range.end;
    }
    return target;
  }

  // ── ITrickPlayControl ────────────────────────────────────────────────────

  public skip(direction: Direction): void {
    const step =
      direction === Direction.Forward
        ? this.mConfig.forwardStep
        : this.mConfig.backwardStep;
    const target = this.clampToSeekRange(
      this.mPlayer.getCurrentTime() + direction * step
    );
    this.mPlayer.pause();
    this.mPlayer.seek(target);
    Log.d(
      EmulatedTrickplayControlImpl.TAG,
      "skip() direction=" + direction + " target=" + target
    );
  }

  public fastForward(direction: Direction, level: SpeedLevel): void {
    const delta = SPEED_VALUE[level] * direction;
    const intervalMs = this.mConfig.tickIntervalMs;
    Log.d(
      EmulatedTrickplayControlImpl.TAG,
      "fastForward() direction=" +
        direction +
        " level=" +
        level +
        " delta=" +
        delta
    );
    this.mPlayer.pause();
    this.mPlayer.setMuted(true);
    this.startTick(() => {
      const target = this.clampToSeekRange(
        this.mPlayer.getCurrentTime() + delta
      );
      this.mPlayer.seek(target);
    }, intervalMs);
  }

  public onScrubBegin(_positionSec: number): void {
    // No-op for emulated mode. UI handles scrub visuals.
  }

  public onScrubMove(positionSec: number): void {
    this.mPlayer.seek(positionSec);
  }

  public onScrubEnd(positionSec: number): void {
    this.mPlayer.seek(positionSec);
  }

  public getTargetPosition(): number {
    return this.mPlayer.getCurrentTime();
  }

  public pauseScrubbing(): void {
    this.clearTick();
    this.mPlayer.setMuted(false);
    // Player stays at whatever position it last seeked to.
    Log.d(EmulatedTrickplayControlImpl.TAG, "pauseScrubbing()");
  }

  public stopScrubbing(): void {
    this.clearTick();
    this.mPlayer.setMuted(false);
    Log.d(EmulatedTrickplayControlImpl.TAG, "stopScrubbing()");
  }
}
